import { CommonModule } from '@angular/common';
import { Component, OnDestroy, computed, effect, input, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import * as QRCode from 'qrcode';
import { RaButtonComponent, RaIconComponent, RaLoadingComponent } from 'ra-ui';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { environment } from '@environments';

/**
 * Modal do QR Code do documento — aberto pelo menu "mais ações" do header.
 *
 * O QR aponta para a página de preview (`/document/:id/preview`) e é gerado aqui no
 * client (lib `qrcode`), sem backend: o caso de uso é imprimir o código e colar no
 * objeto vistoriado (um equipamento, um imóvel), então o modal oferece imprimir e
 * salvar como PNG.
 *
 * ECC nível M e margem de 4 módulos (quiet zone) — o código nasce para papel, onde
 * dobra/impressão fraca comem parte do símbolo.
 */
@Component({
    selector: 'modal-document-qrcode',
    templateUrl: 'modal-document-qrcode.component.html',
    styleUrl: 'modal-document-qrcode.component.scss',
    imports: [CommonModule, RaModalComponent, RaButtonComponent, RaIconComponent, RaLoadingComponent, TranslatePipe],
})
export class ModalDocumentQrcodeComponent implements OnDestroy {
    public documentId = input.required<string>();
    public documentTitle = input<string>('');
    /** Documento privado? Só muda o aviso — o QR é gerado do mesmo jeito. */
    public isPrivate = input<boolean>(false);
    public close = output<void>();

    /** PNG do QR como data URL — também é o href do "Salvar PNG". */
    public qrDataUrl = signal<string | null>(null);

    /** URL de preview — a mesma do botão "Visualizar" do header. */
    public previewUrl = computed(
        () => `${environment.PLATFORM_BASE}document/${this.documentId()}/preview`,
    );

    /** Nome do arquivo do download: título saneado + sufixo. */
    public downloadFileName = computed(() => {
        const slug = (this.documentTitle() || 'documento')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // tira acentos
            .replace(/[^a-zA-Z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase();
        return `${slug || 'documento'}-qrcode.png`;
    });

    constructor(private readonly _translate: TranslateService) {
        // Gera o QR quando o documentId chega (o modal só monta com documento carregado,
        // mas o effect cobre também um eventual re-id).
        effect(() => {
            const url = this.previewUrl();
            // 512px: nítido no retina do modal e sobra resolução para imprimir ~5 cm.
            QRCode.toDataURL(url, { errorCorrectionLevel: 'M', width: 512, margin: 4 })
                .then(dataUrl => this.qrDataUrl.set(dataUrl))
                .catch(() => this.qrDataUrl.set(null));
        });
    }

    /** Iframe invisível usado na impressão — vive até o próximo print ou o destroy. */
    private _printFrame?: HTMLIFrameElement;

    /**
     * Imprime via iframe INVISÍVEL: só o QR, o título e a dica — nada do app em volta
     * e sem aba nova piscando para o usuário (antes era um `window.open` que ficava
     * aberto em `about:blank` depois do diálogo).
     * `onload` + `print()` para o QR (img data URL) já estar pintado.
     */
    public print(): void {
        const dataUrl = this.qrDataUrl();
        if (!dataUrl) return;

        // Um print por vez: derruba o iframe de uma impressão anterior.
        this._printFrame?.remove();

        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(iframe);
        this._printFrame = iframe;

        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        if (!doc || !win) {
            iframe.remove();
            return;
        }

        const title = this._escapeHtml(this.documentTitle());
        const hint = this._escapeHtml(
            this._translate.instant('document.edit.documentQrCode.printHint'),
        );

        // `@page { margin: 0 }` também suprime o cabeçalho/rodapé que o navegador
        // imprime por padrão (data, URL, "1/1") — o respiro vem do padding do body.
        doc.open();
        doc.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
    @page { margin: 0; }
    * { box-sizing: border-box; }
    body {
        margin: 0;
        padding: 16mm;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        font-family: Arial, Helvetica, sans-serif;
        text-align: center;
        /* Fundo explícito: a janela aberta antes do print herda o dark mode do SO */
        background: #fff;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    /* Etiqueta recortável: linha tracejada = guia de corte */
    .label {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5mm;
        padding: 9mm 11mm 7mm;
        border: 1px dashed #b9bec7;
        border-radius: 5mm;
        max-width: 96mm;
    }
    .qr-frame {
        padding: 4mm;
        border: 1px solid #e3e5e9;
        border-radius: 4mm;
        background: #fff;
        line-height: 0;
    }
    img { width: 52mm; height: 52mm; }
    h1 {
        font-size: 15px;
        line-height: 1.35;
        letter-spacing: 0.01em;
        color: #1a1d23;
        margin: 0;
        max-width: 78mm;
    }
    p {
        font-size: 10px;
        line-height: 1.5;
        color: #7a8089;
        margin: 0;
        max-width: 70mm;
    }
    .divider {
        width: 14mm;
        height: 1px;
        background: #e3e5e9;
        margin: 1mm 0;
    }
</style>
</head>
<body>
    <div class="label">
        <div class="qr-frame">
            <img src="${dataUrl}" alt="QR Code" />
        </div>
        <h1>${title}</h1>
        <span class="divider"></span>
        <p>${hint}</p>
    </div>
</body>
</html>`);
        doc.close();

        // `load` dispara depois do `close()`, com a imagem já decodificada.
        iframe.onload = () => {
            win.focus();
            win.print();
        };
    }

    public ngOnDestroy(): void {
        this._printFrame?.remove();
    }

    private _escapeHtml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
}
